import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FiEdit,
  FiUpload,
  FiImage,
  FiX,
  FiSave,
} from "react-icons/fi";

import {
  getQr,
  editQr,
} from "../../store/slices/profile";

import axiosInstance from "../../utils/authUtils";
import {
  API_BASE_URL,
  API_PATHS,
} from "../../utils/constants/api.constants";

const QRCode = () => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);

  const {
    qr = {},
    qrLoading = false,
    editQrLoading = false,
  } = useSelector((state) => state.profile);

  const [selectedImage, setSelectedImage] = useState("");
  const [previewImage, setPreviewImage] = useState("");
  const [imageError, setImageError] = useState("");
  const [uploading, setUploading] = useState(false);

  /* =========================
     GET QR
  ========================= */

  useEffect(() => {
    dispatch(getQr());
  }, [dispatch]);

  /* =========================
     GET QR IMAGE
  ========================= */

  const qrData = qr?.data || qr || {};

  const currentQrImage =
    qrData?.image ||
    qrData?.qrImage ||
    qrData?.url ||
    qrData?.location ||
    "";

  /* =========================
     OPEN FILE PICKER
  ========================= */

  const handleEditClick = () => {
    setImageError("");

    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  /* =========================
     IMAGE UPLOAD
  ========================= */

  const imageUpload = async (e) => {
    setImageError("");

    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    /* FILE TYPE VALIDATION */

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];

    if (!allowedTypes.includes(file.type.toLowerCase())) {
      setImageError(
        "Only JPG, JPEG and PNG files are allowed"
      );

      return;
    }

    /* FILE SIZE VALIDATION */

    if (file.size > 10 * 1024 * 1024) {
      setImageError(
        "Please upload an image less than 10 MB"
      );

      return;
    }

    /* LOCAL PREVIEW */

    const localPreview = URL.createObjectURL(file);

    setPreviewImage(localPreview);

    setUploading(true);

    try {
      const formData = new FormData();

      formData.append("file", file);

      /*
        axiosInstance automatically adds:

        x-access-token

        from sessionStorage
      */

      const response = await axiosInstance.post(
        `${API_BASE_URL}${API_PATHS.userFileUpload}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const data = response.data;

      console.log("Upload Response:", data);

      if (
        (data?.status === 1 || data?.status === true) &&
        data?.data?.location
      ) {
        const uploadedImage = data.data.location;

        setSelectedImage(uploadedImage);

        setPreviewImage(uploadedImage);

        setImageError("");
      } else {
        throw new Error(
          data?.message ||
          "Failed to upload QR image"
        );
      }

    } catch (error) {

      console.error(
        "Image Upload Error:",
        error
      );

      setImageError(
        error.response?.data?.message ||
        error.message ||
        "Something went wrong while uploading the image"
      );

      setPreviewImage("");
      setSelectedImage("");

    } finally {

      setUploading(false);

    }
  };

  /* =========================
     SAVE QR
  ========================= */

  const handleSaveQr = async () => {

    if (!selectedImage) {

      setImageError(
        "Please upload a QR image first"
      );

      return;
    }

    try {

      await dispatch(
        editQr({
          image: selectedImage,
        })
      ).unwrap();

      alert(
        "QR Code updated successfully"
      );

      setSelectedImage("");
      setPreviewImage("");
      setImageError("");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      dispatch(getQr());

    } catch (error) {

      console.error(
        "QR Update Error:",
        error
      );

      alert(
        error ||
        "Failed to update QR Code"
      );

    }
  };

  /* =========================
     CANCEL EDIT
  ========================= */

  const handleCancel = () => {

    setSelectedImage("");
    setPreviewImage("");
    setImageError("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="gi-qr-page">

      <div className="gi-qr-card">

        {/* ================= HEADER ================= */}

        <div className="gi-qr-header">

          <div className="gi-qr-header-left">

            <div className="gi-qr-title-icon">
              <FiImage />
            </div>

            <div>

              <h2>
                Payment QR Code
              </h2>

              <p>
                Manage your payment QR code
              </p>

            </div>

          </div>


          {!selectedImage && (

            <button
              type="button"
              className="btn gi-qr-edit-btn"
              onClick={handleEditClick}
              disabled={uploading}
            >

              <FiEdit />

              {uploading
                ? "Uploading..."
                : "Edit QR"}

            </button>

          )}

        </div>


        {/* ================= CONTENT ================= */}

        <div className="gi-qr-content">

          {qrLoading ? (

            <div className="gi-qr-loading">
              Loading QR Code...
            </div>

          ) : (

            <>

              {/* ================= IMAGE ================= */}

              <div className="gi-qr-image-wrapper">

                {previewImage || currentQrImage ? (

                  <img
                    src={
                      previewImage ||
                      currentQrImage
                    }
                    alt="QR Code"
                    className="gi-qr-image"
                  />

                ) : (

                  <div className="gi-qr-empty">

                    <FiImage />

                    <span>
                      No QR Code Available
                    </span>

                  </div>

                )}

              </div>


              {/* ================= UPLOADING ================= */}

              {uploading && (

                <div className="gi-qr-uploading">
                  Uploading image...
                </div>

              )}


              {/* ================= EDIT MODE ================= */}

              {selectedImage && !uploading && (

                <div className="gi-qr-actions">

                  <button
                    type="button"
                    className="gi-qr-upload-btn"
                    onClick={handleEditClick}
                    disabled={editQrLoading}
                  >

                    <FiUpload />

                    Change Image

                  </button>


                  <button
                    type="button"
                    className="gi-qr-save-btn"
                    onClick={handleSaveQr}
                    disabled={editQrLoading}
                  >

                    <FiSave />

                    {editQrLoading
                      ? "Saving..."
                      : "Save QR"}

                  </button>


                  <button
                    type="button"
                    className="gi-qr-cancel-btn"
                    onClick={handleCancel}
                    disabled={editQrLoading}
                  >

                    <FiX />

                    Cancel

                  </button>

                </div>

              )}


              {/* ================= ERROR ================= */}

              {imageError && (

                <div className="gi-qr-error">

                  {imageError}

                </div>

              )}

            </>

          )}

        </div>


        {/* ================= FILE INPUT ================= */}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          onChange={imageUpload}
          style={{ display: "none" }}
        />

      </div>

    </div>
  );
};

export default QRCode;